import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Building2, ChevronRight, Save, RotateCcw, Share2, Printer, Mail, Layers, Trash2, Download } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { api, API_BASE } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import JsonLd from '../components/common/JsonLd';
import {
    calculateProjection, realityChecks, REGIONAL_PRESETS,
    bandCapRate, bandCoC, bandDSCR,
} from '../lib/projection';

const money = (n) => (isFinite(n) ? n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }) : '—');
const money2 = (n) => (isFinite(n) ? n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }) : '—');
const pct = (n) => (isFinite(n) ? `${n.toFixed(2)}%` : '—');
const num = (n) => (isFinite(n) ? n.toFixed(2) : '—');

const DEFAULT_INPUT = {
    deal_type: 'resale',
    property_name: '', region: 'hamilton', municipality: '', neighbourhood: '', property_type: 'detached',
    purchase_price: 725000, square_footage: '', condo_fee: 0, parking_income: 0, locker_income: 0,
    down_payment_pct: 20, renovation_furnishing: 5000,
    include_toronto_mltt: false,
    legal_fees: 1800, inspection_appraisal: 700, mortgage_insurance: 0,
    financing_fees: 500, reserve_fund: 5000, other_closing: 0,
    interest_rate: 4.75, amortization_years: 30, payment_frequency: 'monthly',
    rate_type: 'fixed', stress_rate: 5.75,
    monthly_rent: 3400, other_monthly_income: 0,
    annual_rent_growth_pct: 3.0, vacancy_pct: 6, bad_debt_pct: 1,
    annual_property_tax: 8100, annual_insurance: 1580,
    property_management_pct: 8, utilities_annual: 0,
    repairs_maintenance_annual: 3600, capex_reserve_annual: 3600,
    licensing_annual: 0, misc_annual: 300,
    expense_inflation_pct: 3.0,
    appreciation_pct: 3.5, selling_commission_pct: 4.0,
    selling_legal_fees: 1800, mortgage_discharge_fee: 350,
    capital_improvements: 0, holding_years: 10,
    // Pre-construction
    years_until_completion: 3, deposits_paid_before_closing: 145000,
    expected_value_at_completion: 850000, expected_rent_at_completion: 2650,
    interim_occupancy_costs: 0, development_charges: 0, assignment_costs: 0,
};

const LS_KEY = 'bn.projection.v1';
const LS_SCENARIOS_KEY = 'bn.projection.scenarios.v1';
const HOLDING_PRESETS = [1, 3, 5, 10, 15, 20, 25];

function loadScenarios() {
    try { return JSON.parse(localStorage.getItem(LS_SCENARIOS_KEY) || '[]'); }
    catch (_e) { return []; }
}
function saveScenarios(list) {
    try { localStorage.setItem(LS_SCENARIOS_KEY, JSON.stringify(list.slice(0, 3))); } catch (_e) { /* noop */ }
}

export default function InvestmentProjectionCalculator() {
    const [inp, setInp] = useState(() => {
        // Try URL first, then localStorage, then defaults
        try {
            const q = new URLSearchParams(window.location.search).get('s');
            if (q) return { ...DEFAULT_INPUT, ...JSON.parse(atob(q)) };
        } catch (_e) { /* noop */ }
        try {
            const ls = localStorage.getItem(LS_KEY);
            if (ls) return { ...DEFAULT_INPUT, ...JSON.parse(ls) };
        } catch (_e) { /* noop */ }
        return DEFAULT_INPUT;
    });

    const set = (k) => (e) => {
        const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
        setInp((p) => ({ ...p, [k]: typeof v === 'string' && v !== '' && !isNaN(v) ? Number(v) : v }));
    };
    const setStr = (k) => (e) => setInp((p) => ({ ...p, [k]: e.target.value }));

    const result = useMemo(() => calculateProjection(inp), [inp]);
    const flags = useMemo(() => realityChecks(result, inp), [result, inp]);

    // Compare mode (up to 3 saved scenarios)
    const [scenarios, setScenarios] = useState(() => loadScenarios());
    const [compareOpen, setCompareOpen] = useState(false);

    function saveScenario() {
        const label = prompt('Name this scenario (e.g. "Hamilton detached · 4.75%")');
        if (!label) return;
        const next = [
            { id: Date.now(), label, inputs: inp, savedAt: new Date().toISOString() },
            ...scenarios,
        ].slice(0, 3);
        setScenarios(next); saveScenarios(next);
    }
    function loadScenario(s) { setInp({ ...DEFAULT_INPUT, ...s.inputs }); }
    function removeScenario(id) {
        const next = scenarios.filter((s) => s.id !== id);
        setScenarios(next); saveScenarios(next);
    }

    // Comparison milestones — clamped to holding_years
    const compareYears = [1, 5, 10, 25].filter((y) => y <= inp.holding_years);
    const compareRows = compareYears.map((y) => result.table[y - 1]).filter(Boolean);

    // Auto-save
    useEffect(() => {
        try { localStorage.setItem(LS_KEY, JSON.stringify(inp)); } catch (_e) { /* noop */ }
    }, [inp]);

    function applyRegionalPreset(regionSlug) {
        const p = REGIONAL_PRESETS[regionSlug];
        if (!p) return;
        setInp((s) => ({
            ...s, region: regionSlug,
            annual_property_tax: Math.round(s.purchase_price * (p.property_tax_pct_of_price / 100)),
            monthly_rent: p.median_rent_2br,
            vacancy_pct: p.vacancy_pct,
            annual_insurance: p.insurance_annual,
        }));
    }

    function reset() {
        if (!window.confirm('Reset to default assumptions?')) return;
        setInp(DEFAULT_INPUT);
        try { localStorage.removeItem(LS_KEY); } catch (_e) { /* noop */ }
    }

    function shareLink() {
        try {
            const s = btoa(JSON.stringify(inp));
            const url = `${window.location.origin}${window.location.pathname}?s=${s}`;
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(url)
                    .then(() => alert('Shareable link copied to clipboard.'))
                    .catch(() => window.prompt('Copy the link below:', url));
            } else {
                window.prompt('Copy the link below:', url);
            }
        } catch (_e) { alert('Could not create share link.'); }
    }

    return (
        <div className="bg-white">
            <SEOHead
                title="Southern Ontario Real Estate Investment Calculator"
                description="Estimate cash flow, cap rate, mortgage paydown, appreciation, equity growth and projected real estate investment returns across Southern Ontario."
                path="/investor-centre/investment-projection-calculator"
            />
            <JsonLd data={[
                {
                    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: 'BestNeighbourhoods.ca', item: 'https://bestneighbourhoods.ca/' },
                        { '@type': 'ListItem', position: 2, name: 'Investor Centre', item: 'https://bestneighbourhoods.ca/investor-centre' },
                        { '@type': 'ListItem', position: 3, name: 'Investment Projection Calculator', item: 'https://bestneighbourhoods.ca/investor-centre/investment-projection-calculator' },
                    ],
                },
                {
                    '@context': 'https://schema.org', '@type': 'WebApplication',
                    name: 'Advanced Real Estate Investment Projection Calculator',
                    applicationCategory: 'FinanceApplication',
                    operatingSystem: 'Web',
                    url: 'https://bestneighbourhoods.ca/investor-centre/investment-projection-calculator',
                    offers: { '@type': 'Offer', price: '0', priceCurrency: 'CAD' },
                    description: 'Model the full financial performance of a Southern Ontario investment property over 1 to 25 years — income, expenses, mortgage amortisation, appreciation, equity growth and total return.',
                },
                {
                    '@context': 'https://schema.org', '@type': 'FAQPage',
                    mainEntity: [
                        { '@type': 'Question', name: 'What is a defensible cap rate for Southern Ontario?', acceptedAnswer: { '@type': 'Answer', text: '3.5-4.5% in Halton (appreciation market), 4.0-5.0% in Durham and Niagara, 4.5-5.5% in Hamilton and Brantford (yield markets). Below 3% signals overpayment; above 6% signals hidden operational risk.' } },
                        { '@type': 'Question', name: 'What rate should I stress-test at?', acceptedAnswer: { '@type': 'Answer', text: 'Model every acquisition against the Bank of Canada policy rate + 1.5% (typically ~5.5-6.0% in 2026). If the deal still cash-flows there, it is a real deal — not a rate bet.' } },
                        { '@type': 'Question', name: 'How is total return separated in the results?', acceptedAnswer: { '@type': 'Answer', text: 'Cash flow, principal paid down, appreciation, and selling costs are all displayed separately. Appreciation is never combined with cash flow into a single unexplained number.' } },
                    ],
                },
            ]} />

            {/* ==================== HEADER ==================== */}
            <section className="border-b border-neutral-200 bg-[#FAFAF8]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-12">
                    <div className="text-[11px] font-mono-data tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-2">
                        <Link to="/investor-centre" className="hover:text-black">Investor Centre</Link>
                        <ChevronRight className="w-3 h-3" />
                        <span>Investment Projection Calculator</span>
                    </div>
                    <h1 className="font-editorial mt-6 leading-[1.03] text-black" style={{ fontSize: 'clamp(36px, 5.5vw, 68px)', letterSpacing: '-0.03em' }} data-testid="proj-title">
                        Advanced Real Estate Investment Projection Calculator
                    </h1>
                    <p className="mt-5 font-editorial text-neutral-700 max-w-3xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                        Model the full financial performance of a Southern Ontario investment property
                        over 1 to 25 years. Rental income, operating expenses, mortgage amortisation,
                        appreciation, equity growth and total return — every component displayed
                        separately, no black-box numbers.
                    </p>

                    {/* Toolbar */}
                    <div className="mt-8 flex flex-wrap gap-2 print:hidden">
                        <ToolbarBtn onClick={saveScenario} icon={<Save className="w-3.5 h-3.5" />} label={`Save scenario (${scenarios.length}/3)`} testid="proj-save-scenario" />
                        <ToolbarBtn onClick={() => setCompareOpen(true)} icon={<Layers className="w-3.5 h-3.5" />} label={`Compare · ${scenarios.length}`} testid="proj-compare-open" disabled={scenarios.length < 2} />
                        <ToolbarBtn onClick={shareLink} icon={<Share2 className="w-3.5 h-3.5" />} label="Share link" testid="proj-share" />
                        <ToolbarBtn onClick={() => window.print()} icon={<Printer className="w-3.5 h-3.5" />} label="Print / Save PDF" testid="proj-print" />
                        <ToolbarBtn onClick={reset} icon={<RotateCcw className="w-3.5 h-3.5" />} label="Reset" testid="proj-reset" />
                    </div>

                    {/* Saved scenarios strip */}
                    {scenarios.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2 print:hidden" data-testid="proj-scenarios">
                            {scenarios.map((s) => (
                                <div key={s.id} className="flex items-center border border-neutral-300 bg-white text-[12px]">
                                    <button onClick={() => loadScenario(s)} className="px-3 py-1.5 hover:bg-neutral-100">
                                        {s.label}
                                    </button>
                                    <button onClick={() => removeScenario(s.id)} className="px-2 py-1.5 border-l border-neutral-300 text-neutral-500 hover:text-red-600" title="Remove">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ==================== BODY ==================== */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* ---- INPUT COLUMN ---- */}
                <div className="lg:col-span-6 xl:col-span-5 space-y-8" data-testid="proj-inputs">
                    {/* Deal type */}
                    <Section title="Deal type">
                        <div className="flex gap-0 border border-neutral-300">
                            {[{ v: 'resale', l: 'Resale property' }, { v: 'preconstruction', l: 'Pre-construction' }].map((o) => (
                                <button key={o.v} type="button" onClick={() => setInp((p) => ({ ...p, deal_type: o.v }))}
                                    data-testid={`proj-deal-${o.v}`}
                                    className={`flex-1 px-4 py-3 text-[13px] font-mono-data tracking-wider uppercase transition-colors ${inp.deal_type === o.v ? 'bg-brand-navy text-white' : 'bg-white text-neutral-600 hover:bg-neutral-100'}`}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </Section>

                    {/* Property */}
                    <Section title="Property">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Name / address" span={2}><input value={inp.property_name} onChange={setStr('property_name')} className={inputCls} /></Field>
                            <Field label="Region">
                                <select value={inp.region} onChange={(e) => applyRegionalPreset(e.target.value)} className={inputCls} data-testid="proj-region">
                                    {Object.entries(REGIONAL_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </Field>
                            <Field label="Municipality"><input value={inp.municipality} onChange={setStr('municipality')} className={inputCls} /></Field>
                            <Field label="Neighbourhood"><input value={inp.neighbourhood} onChange={setStr('neighbourhood')} className={inputCls} /></Field>
                            <Field label="Property type">
                                <select value={inp.property_type} onChange={setStr('property_type')} className={inputCls}>
                                    <option value="detached">Detached</option><option value="semi">Semi-detached</option>
                                    <option value="townhouse">Townhouse</option><option value="condo">Condo</option><option value="mixed">Mixed / other</option>
                                </select>
                            </Field>
                            <Field label="Purchase price">
                                <MoneyInput v={inp.purchase_price} onChange={set('purchase_price')} testid="proj-price" />
                            </Field>
                            <Field label="Square footage"><input type="number" value={inp.square_footage} onChange={set('square_footage')} className={inputCls} /></Field>
                            <Field label="Monthly condo fee"><MoneyInput v={inp.condo_fee} onChange={set('condo_fee')} /></Field>
                            <Field label="Parking income (mo)"><MoneyInput v={inp.parking_income} onChange={set('parking_income')} /></Field>
                            <Field label="Locker income (mo)"><MoneyInput v={inp.locker_income} onChange={set('locker_income')} /></Field>
                        </div>
                    </Section>

                    {/* Pre-construction extras */}
                    {inp.deal_type === 'preconstruction' && (
                        <Section title="Pre-construction" testid="proj-precon">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Years until completion"><input type="number" value={inp.years_until_completion} onChange={set('years_until_completion')} className={inputCls} /></Field>
                                <Field label="Total deposits paid"><MoneyInput v={inp.deposits_paid_before_closing} onChange={set('deposits_paid_before_closing')} /></Field>
                                <Field label="Expected value at completion"><MoneyInput v={inp.expected_value_at_completion} onChange={set('expected_value_at_completion')} /></Field>
                                <Field label="Expected rent at completion"><MoneyInput v={inp.expected_rent_at_completion} onChange={set('expected_rent_at_completion')} /></Field>
                                <Field label="Interim occupancy (mo)"><MoneyInput v={inp.interim_occupancy_costs} onChange={set('interim_occupancy_costs')} /></Field>
                                <Field label="Development charges"><MoneyInput v={inp.development_charges} onChange={set('development_charges')} /></Field>
                                <Field label="Assignment costs"><MoneyInput v={inp.assignment_costs} onChange={set('assignment_costs')} /></Field>
                            </div>
                        </Section>
                    )}

                    {/* Acquisition costs */}
                    <Section title="Acquisition costs">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Down payment %"><PctInput v={inp.down_payment_pct} onChange={set('down_payment_pct')} testid="proj-down" /></Field>
                            <Field label="Renovation / furnishing"><MoneyInput v={inp.renovation_furnishing} onChange={set('renovation_furnishing')} /></Field>
                            <Field label="Toronto MLTT" span={2}>
                                <label className="flex items-center gap-2 text-[13px]">
                                    <input type="checkbox" checked={!!inp.include_toronto_mltt} onChange={set('include_toronto_mltt')} data-testid="proj-mltt" /> Add Toronto municipal land transfer tax
                                </label>
                            </Field>
                            <Field label="Legal fees"><MoneyInput v={inp.legal_fees} onChange={set('legal_fees')} /></Field>
                            <Field label="Inspection / appraisal"><MoneyInput v={inp.inspection_appraisal} onChange={set('inspection_appraisal')} /></Field>
                            <Field label="Mortgage insurance"><MoneyInput v={inp.mortgage_insurance} onChange={set('mortgage_insurance')} /></Field>
                            <Field label="Financing fees"><MoneyInput v={inp.financing_fees} onChange={set('financing_fees')} /></Field>
                            <Field label="Initial reserve fund"><MoneyInput v={inp.reserve_fund} onChange={set('reserve_fund')} /></Field>
                            <Field label="Other closing costs"><MoneyInput v={inp.other_closing} onChange={set('other_closing')} /></Field>
                        </div>
                    </Section>

                    {/* Financing */}
                    <Section title="Financing">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Interest rate"><PctInput v={inp.interest_rate} onChange={set('interest_rate')} testid="proj-rate" /></Field>
                            <Field label="Amortisation (years)"><input type="number" value={inp.amortization_years} onChange={set('amortization_years')} className={inputCls} /></Field>
                            <Field label="Payment frequency">
                                <select value={inp.payment_frequency} onChange={setStr('payment_frequency')} className={inputCls}>
                                    <option value="monthly">Monthly</option><option value="biweekly">Bi-weekly</option><option value="weekly">Weekly</option>
                                </select>
                            </Field>
                            <Field label="Rate type">
                                <select value={inp.rate_type} onChange={setStr('rate_type')} className={inputCls}>
                                    <option value="fixed">Fixed</option><option value="variable">Variable</option>
                                </select>
                            </Field>
                            <Field label="Stress-test rate %" span={2}><PctInput v={inp.stress_rate} onChange={set('stress_rate')} /></Field>
                        </div>
                    </Section>

                    {/* Income */}
                    <Section title="Income">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Monthly rent"><MoneyInput v={inp.monthly_rent} onChange={set('monthly_rent')} testid="proj-rent" /></Field>
                            <Field label="Other monthly income"><MoneyInput v={inp.other_monthly_income} onChange={set('other_monthly_income')} /></Field>
                            <Field label="Annual rent growth %"><PctInput v={inp.annual_rent_growth_pct} onChange={set('annual_rent_growth_pct')} /></Field>
                            <Field label="Vacancy %"><PctInput v={inp.vacancy_pct} onChange={set('vacancy_pct')} /></Field>
                            <Field label="Bad-debt %"><PctInput v={inp.bad_debt_pct} onChange={set('bad_debt_pct')} /></Field>
                        </div>
                    </Section>

                    {/* Operating */}
                    <Section title="Operating expenses">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Property tax (annual)"><MoneyInput v={inp.annual_property_tax} onChange={set('annual_property_tax')} /></Field>
                            <Field label="Insurance (annual)"><MoneyInput v={inp.annual_insurance} onChange={set('annual_insurance')} /></Field>
                            <Field label="Property management %"><PctInput v={inp.property_management_pct} onChange={set('property_management_pct')} /></Field>
                            <Field label="Utilities (annual)"><MoneyInput v={inp.utilities_annual} onChange={set('utilities_annual')} /></Field>
                            <Field label="Repairs & maintenance"><MoneyInput v={inp.repairs_maintenance_annual} onChange={set('repairs_maintenance_annual')} /></Field>
                            <Field label="Capex reserve"><MoneyInput v={inp.capex_reserve_annual} onChange={set('capex_reserve_annual')} /></Field>
                            <Field label="Licensing / compliance"><MoneyInput v={inp.licensing_annual} onChange={set('licensing_annual')} /></Field>
                            <Field label="Miscellaneous"><MoneyInput v={inp.misc_annual} onChange={set('misc_annual')} /></Field>
                            <Field label="Expense inflation %" span={2}><PctInput v={inp.expense_inflation_pct} onChange={set('expense_inflation_pct')} /></Field>
                        </div>
                    </Section>

                    {/* Projection */}
                    <Section title="Projection assumptions">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Annual appreciation %"><PctInput v={inp.appreciation_pct} onChange={set('appreciation_pct')} testid="proj-app" /></Field>
                            <Field label="Selling commission %"><PctInput v={inp.selling_commission_pct} onChange={set('selling_commission_pct')} /></Field>
                            <Field label="Selling legal fees"><MoneyInput v={inp.selling_legal_fees} onChange={set('selling_legal_fees')} /></Field>
                            <Field label="Mortgage discharge fee"><MoneyInput v={inp.mortgage_discharge_fee} onChange={set('mortgage_discharge_fee')} /></Field>
                            <Field label="Capital improvements" span={2}><MoneyInput v={inp.capital_improvements} onChange={set('capital_improvements')} /></Field>
                            <Field label="Holding period" span={2}>
                                <div className="flex flex-wrap gap-1">
                                    {HOLDING_PRESETS.map((y) => (
                                        <button key={y} type="button" onClick={() => setInp((p) => ({ ...p, holding_years: y }))}
                                            data-testid={`proj-hold-${y}`}
                                            className={`px-3 py-1.5 text-[12px] font-mono-data border ${inp.holding_years === y ? 'bg-brand-navy text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-brand-navy'}`}>
                                            Y{y}
                                        </button>
                                    ))}
                                    <input type="number" value={inp.holding_years} onChange={set('holding_years')} className={`${inputCls} w-20`} />
                                </div>
                            </Field>
                        </div>
                    </Section>
                </div>

                {/* ---- RESULTS COLUMN ---- */}
                <div className="lg:col-span-6 xl:col-span-7 space-y-8" data-testid="proj-results">
                    <ResultsDashboard r={result} inp={inp} />
                    <ComparisonTable rows={compareRows} initialCash={result.total_initial_cash} />
                    <ChartBlock r={result} inp={inp} />
                    <RealityCheck flags={flags} />
                    <LeadForm inp={inp} result={result} />

                    <div className="text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-200 pt-6">
                        This calculator provides estimates for educational and planning purposes only.
                        Results depend on the assumptions entered and are not a guarantee of future
                        performance. Confirm financing, rents, expenses, taxes, legal requirements,
                        and investment suitability with qualified professionals.
                    </div>
                </div>
            </section>

            {compareOpen && <CompareModal scenarios={scenarios} onClose={() => setCompareOpen(false)} />}
        </div>
    );
}

// ============================================================================
// Compare mode — up to 3 scenarios side-by-side
// ============================================================================
function CompareModal({ scenarios, onClose }) {
    const computed = scenarios.map((s) => ({
        label: s.label,
        inputs: s.inputs,
        result: calculateProjection({ ...DEFAULT_INPUT, ...s.inputs }),
    }));

    // Chart data — total profit at end of each scenario's holding period
    const chartData = computed.map((c) => ({
        name: c.label.length > 22 ? c.label.slice(0, 22) + '…' : c.label,
        'Cash flow': Math.round(c.result.end?.cumulative_cash_flow || 0),
        'Principal': Math.round(c.result.end?.principal_paid_cum || 0),
        'Appreciation': Math.round(c.result.end?.appreciation || 0),
        'Selling costs': -Math.round(c.result.end?.total_selling_costs || 0),
    }));

    const rows = [
        ['Purchase price', (c) => money(c.result.purchase_price)],
        ['Holding period', (c) => `${c.result.holding_years} yrs`],
        ['Initial cash required', (c) => money(c.result.total_initial_cash)],
        ['Mortgage amount', (c) => money(c.result.mortgage_amount)],
        ['Monthly mortgage payment', (c) => money(c.result.monthly_mortgage_payment)],
        ['NOI (Y1)', (c) => money(c.result.noi_y1)],
        ['Monthly cash flow (Y1)', (c) => money(c.result.monthly_cash_flow_y1)],
        ['Cap rate', (c) => pct(c.result.cap_rate_pct)],
        ['Cash-on-cash (Y1)', (c) => pct(c.result.coc_pct)],
        ['DSCR (Y1)', (c) => num(c.result.dscr)],
        ['Break-even occupancy', (c) => pct(c.result.break_even_occ_pct)],
        [`Equity · end`, (c) => money(c.result.end?.equity)],
        [`Total profit · end`, (c) => money(c.result.end?.total_profit)],
        [`Annualised · end`, (c) => pct(c.result.end?.annualised)],
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8 print:hidden" onClick={onClose} data-testid="proj-compare-modal">
            <div className="bg-white w-full max-w-[1200px] mx-4 my-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 bg-brand-navy text-white">
                    <div>
                        <div className="text-[10.5px] font-mono-data tracking-widest uppercase text-white/60">Compare · up to 3 scenarios</div>
                        <div className="font-editorial mt-1" style={{ fontSize: 20 }}>Side-by-side projection comparison</div>
                    </div>
                    <button onClick={onClose} data-testid="proj-compare-close" className="text-white/80 hover:text-white text-[13px] font-mono-data tracking-widest uppercase">Close</button>
                </div>

                <div className="p-5 lg:p-8">
                    <div className="border border-neutral-200 overflow-x-auto">
                        <table className="w-full text-[13.5px]">
                            <thead className="bg-[#FAFAF8] border-b border-neutral-200 text-[10.5px] uppercase tracking-widest text-neutral-500 font-mono-data">
                                <tr>
                                    <th className="p-3 text-left">Metric</th>
                                    {computed.map((c, i) => <th key={i} className="p-3 text-right">{c.label}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(([label, fn], i) => (
                                    <tr key={i} className="border-b border-neutral-100 last:border-none">
                                        <td className="p-3 text-neutral-700">{label}</td>
                                        {computed.map((c, j) => <td key={j} className="p-3 text-right font-mono-data tabular">{fn(c)}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 border border-neutral-200 bg-white p-4">
                        <div className="text-[10.5px] font-mono-data tracking-widest uppercase text-neutral-500 mb-3">Total return breakdown · by scenario</div>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                                <Tooltip formatter={(v) => money(v)} />
                                <Legend />
                                <Bar dataKey="Cash flow" stackId="a" fill="#2F6B47" />
                                <Bar dataKey="Principal" stackId="a" fill="#4A7A5F" />
                                <Bar dataKey="Appreciation" stackId="a" fill="#B87A2C" />
                                <Bar dataKey="Selling costs" stackId="a" fill="#8B4A3A" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// UI primitives
// ============================================================================
const inputCls = 'w-full border border-neutral-300 bg-white px-3 py-2 text-[13.5px] font-mono-data focus:outline-none focus:border-black transition-colors';

function Section({ title, children, testid }) {
    return (
        <div className="border-t border-neutral-200 pt-6" data-testid={testid}>
            <div className="text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data mb-4">{title}</div>
            {children}
        </div>
    );
}
function Field({ label, children, span }) {
    return (
        <label className={`block ${span === 2 ? 'col-span-2' : ''}`}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-mono-data mb-1">{label}</div>
            {children}
        </label>
    );
}
function MoneyInput({ v, onChange, testid }) {
    return (
        <div className="flex border border-neutral-300 focus-within:border-black">
            <span className="px-2 py-2 text-[12px] text-neutral-500 font-mono-data border-r border-neutral-200 bg-[#FAFAF8]">$</span>
            <input type="number" value={v ?? 0} onChange={onChange} data-testid={testid} className="flex-1 min-w-0 px-2 py-2 text-[13.5px] font-mono-data tabular focus:outline-none" />
        </div>
    );
}
function PctInput({ v, onChange, testid }) {
    return (
        <div className="flex border border-neutral-300 focus-within:border-black">
            <input type="number" step="0.1" value={v ?? 0} onChange={onChange} data-testid={testid} className="flex-1 min-w-0 px-2 py-2 text-[13.5px] font-mono-data tabular focus:outline-none" />
            <span className="px-2 py-2 text-[12px] text-neutral-500 font-mono-data border-l border-neutral-200 bg-[#FAFAF8]">%</span>
        </div>
    );
}
function ToolbarBtn({ onClick, icon, label, testid, muted, disabled }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
            className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-[12px] font-mono-data tracking-wider ${muted || disabled ? 'border-neutral-200 text-neutral-400 cursor-not-allowed' : 'border-neutral-300 text-neutral-800 hover:border-brand-navy hover:bg-brand-navy hover:text-white'} transition-colors`}
           >
            {icon}{label}
        </button>
    );
}

// ============================================================================
// Results dashboard
// ============================================================================
function ResultsDashboard({ r, inp }) {
    const y5 = r.table[Math.min(4, r.table.length - 1)];
    const y10 = r.table[Math.min(9, r.table.length - 1)];
    const end = r.end;
    const tiles = [
        { l: 'Initial cash required', v: money(r.total_initial_cash) },
        { l: 'Monthly cash flow (Y1)', v: money(r.monthly_cash_flow_y1), tone: r.monthly_cash_flow_y1 >= 0 ? 'up' : 'down' },
        { l: 'NOI (Y1)', v: money(r.noi_y1) },
        { l: 'Cap rate', v: pct(r.cap_rate_pct) },
        { l: 'Cash-on-cash (Y1)', v: pct(r.coc_pct), tone: r.coc_pct >= 0 ? 'up' : 'down' },
        { l: 'DSCR (Y1)', v: num(r.dscr) },
        { l: 'Y5 projected equity', v: money(y5?.equity) },
        { l: 'Y10 projected equity', v: money(y10?.equity) },
        { l: `Total return · Y${end?.year || inp.holding_years}`, v: money(end?.total_profit) },
    ];
    return (
        <div>
            <div className="text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data mb-4">Results dashboard</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-t border-l border-neutral-200" data-testid="proj-dashboard">
                {tiles.map((t, i) => (
                    <div key={i} className="border-r border-b border-neutral-200 p-4 bg-white">
                        <div className="text-[10.5px] font-mono-data uppercase tracking-widest text-neutral-500">{t.l}</div>
                        <div className={`font-mono-data tabular mt-2 ${t.tone === 'up' ? 'text-emerald-700' : t.tone === 'down' ? 'text-red-700' : 'text-black'}`} style={{ fontSize: 22 }}>{t.v}</div>
                    </div>
                ))}
            </div>
            <div className="mt-3 text-[11.5px] font-mono-data text-neutral-500">
                Break-even occupancy · {pct(r.break_even_occ_pct)} · Gross yield · {pct(r.gross_yield_pct)} · Net yield · {pct(r.net_yield_pct)}
                {r.stressed_annual_debt_service ? ` · Stressed annual DS · ${money(r.stressed_annual_debt_service)}` : ''}
            </div>
        </div>
    );
}

// ============================================================================
// Comparison table Y1 / Y5 / Y10 / Y25
// ============================================================================
function ComparisonTable({ rows, initialCash }) {
    if (!rows.length) return null;
    const headers = ['', ...rows.map((r) => `Year ${r.year}`)];
    const lines = [
        ['Est. property value', (r) => money(r.property_value)],
        ['Monthly rent', (r) => money(r.monthly_rent)],
        ['Annual gross income', (r) => money(r.gross_annual_income)],
        ['Operating expenses', (r) => money(-r.total_opex)],
        ['Net operating income', (r) => money(r.noi)],
        ['Annual mortgage payments', (r) => money(-r.annual_debt_service)],
        ['Net cash flow', (r) => money(r.net_cash_flow)],
        ['Remaining mortgage', (r) => money(r.remaining_mortgage)],
        ['Principal paid down (cum)', (r) => money(r.principal_paid_cum)],
        ['Appreciation (cum)', (r) => money(r.appreciation)],
        ['Estimated equity', (r) => money(r.equity)],
        ['Net proceeds if sold', (r) => money(r.net_sale_proceeds)],
        ['Total profit if sold', (r) => money(r.total_profit)],
        ['Return on invested capital', (r) => pct(r.roic)],
    ];
    return (
        <div>
            <div className="text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data mb-4">Comparison · projected milestones</div>
            <div className="border border-neutral-200 overflow-x-auto" data-testid="proj-compare">
                <table className="w-full text-[13px]">
                    <thead className="bg-[#FAFAF8] border-b border-neutral-200 text-[10.5px] uppercase tracking-widest text-neutral-500 font-mono-data">
                        <tr>{headers.map((h, i) => <th key={i} className={`p-3 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {lines.map(([label, fn], i) => (
                            <tr key={i} className="border-b border-neutral-100 last:border-none">
                                <td className="p-3 text-neutral-700">{label}</td>
                                {rows.map((r, j) => <td key={j} className="p-3 text-right font-mono-data tabular">{fn(r)}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================================
// Charts
// ============================================================================
function ChartBlock({ r, inp }) {
    const data = r.table.map((row) => ({
        year: `Y${row.year}`,
        Value: Math.round(row.property_value),
        Mortgage: Math.round(row.remaining_mortgage),
        Equity: Math.round(row.equity),
        CashFlow: Math.round(row.net_cash_flow),
        CumCF: Math.round(row.cumulative_cash_flow),
    }));
    const end = r.end || {};
    const breakdown = [{
        stage: 'Total return',
        'Cash flow': Math.round(end.cumulative_cash_flow || 0),
        'Principal paid down': Math.round(end.principal_paid_cum || 0),
        'Appreciation': Math.round(end.appreciation || 0),
        'Selling costs': -Math.round(end.total_selling_costs || 0),
        'Initial cash': -Math.round(r.total_initial_cash || 0),
    }];
    return (
        <div className="space-y-6" data-testid="proj-charts">
            <ChartCard title="Property value vs mortgage balance">
                <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                        <Tooltip formatter={(v) => money(v)} />
                        <Area type="monotone" dataKey="Value" stroke="#2F6B47" fill="#2F6B47" fillOpacity={0.15} />
                        <Area type="monotone" dataKey="Mortgage" stroke="#B87A2C" fill="#B87A2C" fillOpacity={0.15} />
                        <Legend />
                    </AreaChart>
                </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Equity growth over time">
                <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                        <Tooltip formatter={(v) => money(v)} />
                        <Area type="monotone" dataKey="Equity" stroke="#2F6B47" fill="#2F6B47" fillOpacity={0.3} />
                    </AreaChart>
                </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Cash flow over time (annual + cumulative)">
                <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                        <Tooltip formatter={(v) => money(v)} />
                        <Bar dataKey="CashFlow" fill="#2F6B47" />
                        <Legend />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>
            <ChartCard title={`Total return breakdown · Year ${end.year || inp.holding_years}`}>
                <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                        <YAxis dataKey="stage" type="category" tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => money(v)} />
                        <Legend />
                        <Bar dataKey="Cash flow" stackId="a" fill="#2F6B47" />
                        <Bar dataKey="Principal paid down" stackId="a" fill="#4A7A5F" />
                        <Bar dataKey="Appreciation" stackId="a" fill="#B87A2C" />
                        <Bar dataKey="Selling costs" stackId="a" fill="#8B4A3A" />
                        <Bar dataKey="Initial cash" stackId="a" fill="#404040" />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>
        </div>
    );
}
function ChartCard({ title, children }) {
    return (
        <div className="border border-neutral-200 bg-white p-4">
            <div className="text-[10.5px] font-mono-data tracking-widest uppercase text-neutral-500 mb-3">{title}</div>
            {children}
        </div>
    );
}

// ============================================================================
// Reality checks
// ============================================================================
function RealityCheck({ flags }) {
    return (
        <div data-testid="proj-reality">
            <div className="text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data mb-4">Investment reality check</div>
            {flags.length === 0 ? (
                <div className="border border-neutral-200 bg-[#FAFAF8] p-4 text-[13.5px] text-neutral-700">
                    No material flags at these assumptions. Still worth pressure-testing with a stricter appreciation and rent-growth scenario.
                </div>
            ) : (
                <ul className="border border-neutral-200 divide-y divide-neutral-200 bg-white">
                    {flags.map((f, i) => (
                        <li key={i} className="p-4">
                            <div className="flex items-center gap-2">
                                <span className={`inline-block w-1.5 h-1.5 ${f.level === 'warn' ? 'bg-red-600' : 'bg-[#B87A2C]'}`} />
                                <span className="text-[10.5px] font-mono-data uppercase tracking-widest text-neutral-500">{f.level === 'warn' ? 'Signal' : 'Note'}</span>
                                <span className="font-editorial text-[15px]">{f.tag}</span>
                            </div>
                            <p className="mt-2 text-[13.5px] leading-relaxed text-neutral-700">{f.body}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ============================================================================
// Lead capture
// ============================================================================
function LeadForm({ inp, result }) {
    const [f, setF] = useState({
        first_name: '', last_name: '', email: '', phone: '',
        investor_experience: 'first-time', capital_band: '100k-250k', timeline: '3-6-months',
        preferred_regions: '', property_type: inp.property_type || 'detached', financing_status: 'not-yet',
        consent: false,
    });
    const [state, setState] = useState('idle'); // idle | sending | done | error
    const [err, setErr] = useState('');
    const [pdfUrl, setPdfUrl] = useState(null);
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

    const summary = {
        total_initial_cash: result.total_initial_cash,
        mortgage_amount: result.mortgage_amount,
        monthly_mortgage_payment: result.monthly_mortgage_payment,
        noi_y1: result.noi_y1,
        annual_cash_flow_y1: result.annual_cash_flow_y1,
        cap_rate_pct: result.cap_rate_pct,
        coc_pct: result.coc_pct,
        dscr: result.dscr,
        end_year: result.end?.year,
        end_equity: result.end?.equity,
        end_total_profit: result.end?.total_profit,
        end_roic: result.end?.roic,
        end_annualised: result.end?.annualised,
        band_cap_rate: bandCapRate(result.cap_rate_pct),
        band_coc: bandCoC(result.coc_pct),
        band_dscr: bandDSCR(result.dscr),
    };

    async function submit(e) {
        e.preventDefault();
        if (!f.consent) { setErr('Please acknowledge the consent checkbox.'); return; }
        setState('sending'); setErr('');
        try {
            const flagsList = realityChecks(result, inp);
            const resp = await api.post('/investors/projection-report', {
                ...f, inputs: inp, summary, result, flags: flagsList,
            });
            // Store pdf_url from the response for the download button
            setPdfUrl(resp?.data?.pdf_url || null);
            setState('done');
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again.');
            setState('error');
        }
    }

    if (state === 'done') {
        async function downloadPdf() {
            try {
                // Prefer the server-stored PDF (identical to the one GHL will email)
                if (pdfUrl) {
                    window.open(`${API_BASE}${pdfUrl.replace(/^\/api/, '')}`, '_blank');
                    return;
                }
                const res = await fetch(`${API_BASE}/investors/projection-report/pdf`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        first_name: f.first_name, last_name: f.last_name, email: f.email,
                        property_type: f.property_type, preferred_regions: f.preferred_regions,
                        inputs: inp, result, flags: realityChecks(result, inp),
                    }),
                });
                if (!res.ok) throw new Error('pdf failed');
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'BestNeighbourhoods-Projection.pdf';
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
            } catch (e) { alert('Download failed. Please try again.'); }
        }
        return (
            <div className="border border-brand-navy bg-brand-navy text-white p-6" data-testid="proj-lead-done">
                <div className="text-[10.5px] font-mono-data tracking-widest uppercase text-white/60">Request received</div>
                <div className="font-editorial mt-3" style={{ fontSize: 24 }}>Your investment projection is ready.</div>
                <p className="mt-3 text-[14px] text-white/85 max-w-xl">
                    Your request is saved and Sam will follow up personally. Download the full PDF now —
                    every input, the per-year table and the reality check.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    <button onClick={downloadPdf} data-testid="proj-lead-pdf"
                        className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-[13.5px] font-medium hover:bg-neutral-200"
                       >
                        <Download className="w-4 h-4" /> Download the PDF now
                    </button>
                    <Link to="/investors/strategy-session" data-testid="proj-lead-cta"
                        className="inline-flex items-center gap-2 border border-white text-white px-5 py-3 text-[13.5px] font-medium hover:bg-brand-gold hover:text-brand-navy"
                       >
                        Book an Investment Strategy Session <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
                <p className="mt-4 text-[12px] text-white/60">
                    Would you like help pressure-testing the assumptions?
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="border border-neutral-200 bg-[#FAFAF8] p-5 lg:p-6 print:hidden" data-testid="proj-lead-form">
            <div className="flex items-center gap-2 text-[10.5px] font-mono-data uppercase tracking-widest text-neutral-500">
                <Mail className="w-3.5 h-3.5" /> Get my investment analysis
            </div>
            <div className="font-editorial mt-3" style={{ fontSize: 22 }}>Get the full report as a PDF.</div>
            <p className="mt-1 text-[13px] text-neutral-600">Includes inputs, per-year table, charts and reality check. No cost, no obligation.</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder="First name" value={f.first_name} onChange={set('first_name')} className={inputCls} data-testid="proj-lead-first" />
                <input required placeholder="Last name"  value={f.last_name} onChange={set('last_name')} className={inputCls} data-testid="proj-lead-last" />
                <input required type="email" placeholder="Email" value={f.email} onChange={set('email')} className={inputCls} data-testid="proj-lead-email" />
                <input required placeholder="Phone" value={f.phone} onChange={set('phone')} className={inputCls} data-testid="proj-lead-phone" />
                <select value={f.investor_experience} onChange={set('investor_experience')} className={inputCls}>
                    <option value="first-time">Experience · First-time investor</option>
                    <option value="one-property">1 property</option>
                    <option value="small-portfolio">2-5 properties</option>
                    <option value="seasoned">6+ properties</option>
                </select>
                <select value={f.capital_band} onChange={set('capital_band')} className={inputCls}>
                    <option value="under-100k">Capital · Under $100k</option>
                    <option value="100k-250k">$100k – $250k</option>
                    <option value="250k-500k">$250k – $500k</option>
                    <option value="500k-1m">$500k – $1M</option>
                    <option value="1m-plus">$1M+</option>
                </select>
                <select value={f.timeline} onChange={set('timeline')} className={inputCls}>
                    <option value="0-3-months">Timeline · 0-3 months</option>
                    <option value="3-6-months">3-6 months</option>
                    <option value="6-12-months">6-12 months</option>
                    <option value="12-plus">12+ months</option>
                    <option value="just-exploring">Just exploring</option>
                </select>
                <input placeholder="Preferred regions (e.g. Hamilton, Brantford)" value={f.preferred_regions} onChange={set('preferred_regions')} className={inputCls} />
                <select value={f.property_type} onChange={set('property_type')} className={inputCls}>
                    <option value="detached">Property type · Detached</option>
                    <option value="semi">Semi-detached</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="condo">Condo</option>
                    <option value="mixed">Mixed / other</option>
                </select>
                <select value={f.financing_status} onChange={set('financing_status')} className={inputCls}>
                    <option value="pre-approved">Financing · Pre-approved</option>
                    <option value="applied">Applied</option>
                    <option value="not-yet">Not yet</option>
                    <option value="cash">Cash purchase</option>
                </select>
            </div>
            <label className="mt-4 flex items-start gap-2 text-[12.5px] text-neutral-700">
                <input required type="checkbox" checked={f.consent} onChange={set('consent')} data-testid="proj-lead-consent" />
                <span>I agree to receive the report and occasional follow-up emails. Unsubscribe anytime.</span>
            </label>
            {err && <div className="mt-3 text-[13px] text-red-600">{err}</div>}
            <button type="submit" disabled={state === 'sending'} data-testid="proj-lead-submit"
                className="mt-5 inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3 text-[14px] font-medium hover:bg-neutral-800 disabled:opacity-60"
               >
                {state === 'sending' ? 'Sending…' : (<>Email me the analysis <ArrowUpRight className="w-4 h-4" /></>)}
            </button>
        </form>
    );
}
